import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setErrorFeedback } from '../../../../../features/feedback/feedbackSlice';
import { doGetQCXRequest } from '../../../../../utils/api/domain/domain-api-utils';
import normalizeProcessosCapa from '../utils/normalizeProcessosCapa';

const useGestaoAVista = (
  produto = 'IMPORTACAO' // 'IMPORTACAO' | 'EXPORTACAO'
) => {
  const dispatch = useDispatch();

  const [isLoadingProcessosCapa, setIsLoadingProcessosCapa] = useState(false);
  const [isLoadingEtapasCapa, setIsLoadingEtapasCapa] = useState(false);
  const [isLoadingProcessos, setIsLoadingProcessos] = useState(false);

  const [processosCapa, setProcessosCapa] = useState({});
  const [etapasCapa, setEtapasCapa] = useState({});
  const [processos, setProcessos] = useState([]);

  const [selectedEstagio, setSelectedEstagio] = useState(undefined);
  const [selectedEtapa, setSelectedEtapa] = useState(undefined);

  const [listConfig, setListConfig] = useState({
    currentPage: 1,
    pageSize: 10,
    filterInputValue: '',
  });

  const filteredProcessos = useMemo(() => {
    const search = listConfig.filterInputValue.toLowerCase();

    const filtered = processos.filter((p) =>
      ['numero', 'importadorNome', 'clienteNome', 'referenciaCliente', 'cnpj', 'status', 'servicoNome'].some((field) =>
        (p[field] || '').toString().toLowerCase().includes(search)
      )
    );

    const start = (listConfig.currentPage - 1) * listConfig.pageSize;
    const paginated = filtered.slice(start, start + listConfig.pageSize);

    return paginated;
  }, [processos, listConfig]);

  const processosCapaConfig = normalizeProcessosCapa(processosCapa, produto);

  const handleSelectedEstagioChange = (v) => {
    setSelectedEtapa(undefined);

    let isSameAsOld = false;

    setSelectedEstagio((prev) => {
      isSameAsOld = prev === v;

      return isSameAsOld ? undefined : v;
    });

    if (isSameAsOld) setSelectedEtapa(undefined);
  };
  const handleSelectedEtapaChange = (v) => {
    setSelectedEstagio(undefined);
    setSelectedEtapa((prev) => (prev === v ? undefined : v));
  }

  const showErrorFeedback = (message) => {
    dispatch(
      setErrorFeedback({
        message,
      })
    );
  };

  const fetchProcessosCapa = useCallback(async () => {
    setIsLoadingProcessosCapa(true);

    try {
      const response = await doGetQCXRequest(
        ['followup', 'gestao-a-vista', 'processos-capa'],
        [{ name: 'produto', value: produto }]
      );

      if (response.status === 200) setProcessosCapa(response.data);
      else showErrorFeedback('Erro ao buscar estágio dos processos.');
    } catch (error) {
      showErrorFeedback('Erro ao buscar estágio dos processos.');
    } finally {
      setIsLoadingProcessosCapa(false);
    }
  }, []);

  const fetchEtapasCapa = useCallback(async () => {
    setIsLoadingEtapasCapa(true);

    try {
      const response = await doGetQCXRequest(
        ['followup', 'gestao-a-vista', 'etapas-capa'],
        [
          { name: 'filtroEstagio', value: selectedEstagio },
          { name: 'produto', value: produto },
        ]
      );

      if (response.status === 200) setEtapasCapa(response.data);
      else showErrorFeedback('Erro ao buscar etapas pendentes.');
    } catch (error) {
      showErrorFeedback('Erro ao buscar etapas pendentes.');
    } finally {
      setIsLoadingEtapasCapa(false);
    }
  }, [selectedEstagio]);

  const fetchProcessos = useCallback(async () => {
    if (!selectedEstagio && !selectedEtapa) return;

    setIsLoadingProcessos(true);

    try {
      const response = await doGetQCXRequest(
        ['followup', 'gestao-a-vista', 'processos'],
        [
          { name: 'filtroEstagio', value: selectedEstagio || '' },
          { name: 'filtroEtapa', value: selectedEtapa || '' },
          { name: 'produto', value: produto },
        ]
      );

      if (response.status === 200) setProcessos(response.data.followUps ?? []);
      else showErrorFeedback('Erro ao buscar processos.');
    } catch (error) {
      showErrorFeedback('Erro ao buscar processos.');
    } finally {
      setIsLoadingProcessos(false);
    }
  }, [selectedEstagio, selectedEtapa]);

  useEffect(() => {
    fetchProcessosCapa();
  }, []);

  useEffect(() => {
    fetchEtapasCapa();
  }, [fetchEtapasCapa]);

  useEffect(() => {
    fetchProcessos();
  }, [fetchProcessos]);

  return {
    selectedEstagio,
    setSelectedEstagio: handleSelectedEstagioChange,
    selectedEtapa,
    setSelectedEtapa: handleSelectedEtapaChange,
    listConfig,
    onListConfigChange: setListConfig,
    isLoadingProcessosCapa,
    isLoadingEtapasCapa,
    isLoadingProcessos,
    processosCapaConfig,
    etapasCapa,
    processosCount: processos.length,
    processos: filteredProcessos,
  };
};

export default useGestaoAVista;
